<template>
  <div class="mt-4" v-if="!loading && claims.length !== 0">
    <div class="flex" >
      <div class="flex-child" v-if="!loading" data-test="claims-status-chart">
        <fusioncharts type="stackedcolumn2d" width="100%" height="400" dataFormat="json" :dataSource="claimsStatus"></fusioncharts>
      </div>
      <div class="flex-child" v-if="!loading" data-test="claims-coverage-chart">
        <fusioncharts type="stackedcolumn2d" width="100%" height="400" dataFormat="json" :dataSource="claimsCoverage"></fusioncharts>
      </div>
    </div>

    <div>
      <Alert v-if="!loading && error" class="mx-4" type="error">{{ error }}</Alert>
      <div class="inner" v-if="!loading && !error">
        <div class="mb-1.5 flex content-start items-start gap-x-2">
          <div class="my-auto flex w-full items-center justify-between">
            <div class="flex items-center gap-2">
              <h2>Claims Detail</h2>
              <DownloadButton @click="download" />
            </div>
            <div class="button-group account-status">
              <div class="join justify-center">
                <input type="radio" name="claimStatusToggle" aria-label="Show All" class="join-item btn btn-sm"
                       @click="claimStatusToggle = ClaimStatus.All; filterClaims()" :checked="claimStatusToggle === ClaimStatus.All"/>
                <input type="radio" name="claimStatusToggle" aria-label="Open" class="join-item btn btn-sm"
                       @click="claimStatusToggle = ClaimStatus.Open; filterClaims()" :checked="claimStatusToggle === ClaimStatus.Open"/>
                <input type="radio" name="claimStatusToggle" aria-label="Closed" class="join-item btn btn-sm"
                       @click="claimStatusToggle = ClaimStatus.Closed; filterClaims()" :checked="claimStatusToggle === ClaimStatus.Closed"/>
              </div>
            </div>
          </div>
        </div>
        <table v-tablesort id="claims-data" data-test="claims-data-table" class="dense mt-4 tower-data">
          <colgroup>
            <col>
            <col v-if="showParentAccount">
            <col>
            <col style="width: 20%">
            <col>
            <col>
            <col>
            <col>
            <col>
            <col>
            <col>
          </colgroup>
          <thead>
            <tr>
              <td v-if="showParentAccount" data-test="accountName">Account Name</td>
              <td data-test="coverage">Coverage</td>
              <td data-test="claimant">Claimant</td>
              <td data-test="status">Status</td>
              <td data-test="carrierPolicyNumber">Carrier Policy Number</td>
              <td data-test="policyPeriod" class="yearspan sorted descending">Policy Period</td>
              <td data-test="lastCloseDate" class="date">Close Date</td>
              <td data-test="retention" class="money">Retention</td>
              <td data-test="totalIncurred" class="money">Total<br/>Incurred</td>
              <td data-test="totalRecovered" class="money">Total<br/>Carrier Paid</td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="claim in filteredClaims" :key="claim.claimID">
              <td v-if="showParentAccount">{{claim.accountName}}</td>
              <td>{{claim.coverage}}</td>
              <td>{{claim.claimant ?? '--'}}</td>
              <td>
                <Chip v-if="claim.status === 'C'" label="CLOSED" variant="outline" size="sm" color-class="border-secondary text-secondary" />
                <Chip v-if="claim.status === 'O'" label="OPEN" variant="outline" size="sm" color-class="border-primary text-primary" />
              </td>

              <td>{{claim.carrierPolicyNumber ?? '--'}}</td>
              <td>{{claim.policyPeriod ?? '--'}}</td>
              <td>{{ filters.date(claim.lastCloseDate === '0001-01-01T00:00:00Z' ? '' : claim.lastCloseDate) ?? '--'}}</td>
              <td>{{numToMoneyString(claim.retention, 0) ?? '--' }}</td>
              <td class="money">{{numToMoneyString(claim.totalIncurred, 0) ?? '--' }}</td>
              <td class="money">{{numToMoneyString(claim.totalRecovered, 0) ?? '--' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div class="no-data" v-else>
    <div v-if="loading && !error" class="space-y-2 p-2">
      <Skeleton
        v-for="i in 8"
        :key="i"
        shape="rectangle"
        class="w-full"
        height="44px" />
    </div>

    <div v-if="!loading">
      <div class="mt-8 flex justify-center">
        <h2 class="text-2xl text-gray-500">No claims found</h2>
      </div>
    </div>
  </div>
</template>

<script>
import { numToMoneyString } from '@/utilities/Money';
import { accountService, parentAccountService } from '@/services/api';
import { DownloadTableToCSV } from '@/utilities/Download';
import { chartPrimaryColor, chartSecondaryColor, stackedSeriesColors } from '@/utilities/Color';
import { ErrorLoadingClaimInformation } from '@/utilities/Errors';
import { filters } from '@/utilities/Filters';
import Alert from '@/components/v2/Alert.vue';
import Chip from '@/components/v2/Chip.vue';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { ClaimStatus } from '@/types/ClaimStatus.ts';

const paletteColors = stackedSeriesColors;
const chartDefaults = {
  animation: '1',
  theme: 'cacs',
  showLegend: true,
  legendNumColumns: 1,
  legendPosition: 'right-top',
  decimals: 0,
  showSum: false,
  minPlotHeightForValue: 10,
  xAxisName: 'Policy Period',
  yAxisName: 'Claims',
};
const claimsStatusChart = {...chartDefaults, caption: 'Claims Status By Policy Period'};
const claimsCoverageChart = {...chartDefaults, caption: 'Claims Coverage Types by Policy Period'};

export default {
  props: {
    account: Object,
    showParentAccount: Boolean
  },
  components: { Alert, Chip, DownloadButton, Skeleton },
  data: () => ({
    ClaimStatus,
    filters,
    claims: [],
    loading: false,
    error: undefined,
    claimStatusToggle: ClaimStatus.All,
    filteredClaims: [],
    claimsStatusData: null,
    claimsCoverageData: null,
  }),
  computed: {
    claimsStatus() {
      return {chart: {...claimsStatusChart, paletteColors}, ...this.claimsStatusData};
    },
    claimsCoverage() {
      return {chart: {...claimsCoverageChart, paletteColors}, ...this.claimsCoverageData};
    },
  },
  methods: {
    numToMoneyString,
    formatPolicyPeriod(policyPeriod) {
      const [start, end] = policyPeriod.split('-');
      if (end && end.length === 4) {
        return `${start}-${end.substring(2)}`;
      }
      return policyPeriod;
    },
    filterClaims() {
      if (this.claimStatusToggle === ClaimStatus.All) {
        this.filteredClaims = this.claims;
      } else if (this.claimStatusToggle === ClaimStatus.Open) {
        this.filteredClaims = this.claims.filter(x => x.status === 'O');
      } else if (this.claimStatusToggle === ClaimStatus.Closed) {
        this.filteredClaims = this.claims.filter(x => x.status === 'C');
      }
    },
    async loadClaims() {
      this.loading = true;
      try {
        const accID = this.account.accountID;
        this.claims = (await (this.showParentAccount ? parentAccountService.GetClaimsByParentAccount(accID) : accountService.GetClaimsForAccount(accID))) || [];

        this.claims = this.claims.map(c => ({
          ...c,
          policyPeriod: this.formatPolicyPeriod(c.policyPeriod),
        }));

        const periods = new Set(this.claims.map(x => x.policyPeriod).sort());

        let sortedPeriods = {categories: [{category: Array.from(periods).sort((a, b) => a - b).map(y => ({label: y.toString()}))}]};
        this.claimsStatusData = {...sortedPeriods};
        this.claimsCoverageData = {...sortedPeriods};

        const allCoverages = new Set(this.claims.map((c) => c.coverage));

        const coverageCount = Array.from(allCoverages).reduce((count, c) => {
          count[c] = 0;
          return count;
        }, {});

        const claimsStatusMap = new Map();
        const sortedPolicyMap = new Map();

        const sortedYears = Array.from(periods).sort((a, b) => a - b);
        sortedYears.forEach(y => {
          claimsStatusMap.set(y, [0, 0, 0]);
          sortedPolicyMap.set(y, {...coverageCount});
        });

        const statusMap = new Map([
            ['O', 0],
            ['C', 1],
            ['R', 2]
        ]);

        this.claims.forEach(claim => {sortedPolicyMap.get(claim.policyPeriod)[claim.coverage]++;});

        const groupedClaims = this.claims.reduce((claimsByPeriod, c) => {
          const period = c.policyPeriod;

          if (!claimsByPeriod.has(period)) {
            claimsByPeriod.set(period, [0, 0]);
          }

          if (statusMap.has(c.status)) {
            claimsByPeriod.get(period)[statusMap.get(c.status)]++;
          }
          return claimsByPeriod;
        }, claimsStatusMap);

        const getStatusValue = (idx) => Array.from(groupedClaims.values()).reduce((acc, claim) => [...acc,
          {
            value: claim[idx].toString(),
            displayValue: '',
            showValue: ''
          }], []);


        this.claimsStatusData.dataset = [
          {seriesname: 'Closed',   color: chartSecondaryColor, data: getStatusValue(1)},
          {seriesname: 'Open',     color: chartPrimaryColor,   data: getStatusValue(0)},
          {seriesname: 'Reopened', color: '',                  data: getStatusValue(2)}
        ];

        this.claimsCoverageData.dataset = Array.from(allCoverages).reduce((acc, seriesname) =>
                [...acc, {
                  seriesname,
                  data: (() => {
                    return Array.from(sortedPolicyMap.values()).reduce((acc, policy) => (
                        [...acc, {value: policy[seriesname] || null}]
                    ), []);
                  })()
                }]
            , []);

        this.filteredClaims = this.claims;
      } catch (err) {
        console.error(err);
        this.error = ErrorLoadingClaimInformation;
      }

      this.loading = false;
    },
    async download(){
      DownloadTableToCSV('#claims-data', this.account.accountName + `-${new Date().toISOString().split('T')[0]}-flp-claims`);
    }
  },
  async created() {
    await this.loadClaims();
  }
};
</script>

<style scoped>
div {
  text-align: center;
}

.flex-child {
    flex: 1;
}

.no-data > h2 {
  padding-top: 1rem;
}
</style>
